"use client";
import { PointerEvent, useRef, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { caseStudyData } from "@/data/CaseStudyData";
import { caseStudyWorkings } from "@/data/CaseStudyPageData";
import { fadeInUp } from "@/lib/animation/variant";
import { CaseStudyProps, CaseStudyWorking } from "@/lib/types";
import { cn } from "@/lib/utils/utils";

interface WorkRowProps {
  study: CaseStudyProps;
  working: CaseStudyWorking;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

const WorkRow = ({ study, working, index, isOpen, onToggle }: WorkRowProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  // The flood grows from wherever the pointer crossed the row, so the colour
  // arrives from the reader's own direction instead of always from the centre.
  const anchorFlood = (event: PointerEvent<HTMLElement>) => {
    const row = rowRef.current;
    if (!row) return;
    const rect = row.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    row.style.setProperty("--mx", `${x.toFixed(1)}%`);
    row.style.setProperty("--my", `${y.toFixed(1)}%`);
  };

  // Every client tone is a light one, so flooded rows keep the dark brand ink
  // rather than flipping to white text on a pale ground.
  const flooded = hovered || isOpen;
  const bodyId = `case-working-${study.slug}`;

  return (
    <motion.div
      ref={rowRef}
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={fadeInUp}
      className="relative border-t border-black/10 last:border-b"
    >
      <h3 className="m-0">
        <button
          type="button"
          onClick={onToggle}
          onPointerEnter={(event) => {
            anchorFlood(event);
            setHovered(true);
          }}
          onPointerLeave={() => setHovered(false)}
          onFocus={() => setHovered(true)}
          onBlur={() => setHovered(false)}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          className="relative block w-full cursor-pointer text-left outline-primary focus-visible:outline-3 focus-visible:-outline-offset-3"
        >
          <span
            aria-hidden
            style={{
              backgroundColor: study.bgColor,
              clipPath: flooded
                ? "circle(142% at var(--mx, 50%) var(--my, 50%))"
                : "circle(0% at var(--mx, 50%) var(--my, 50%))",
            }}
            className="absolute inset-0 transition-[clip-path] duration-[620ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          />

          <span
            className={cn(
              "relative grid items-center gap-2 px-4 py-6 transition-colors duration-400 lg:grid-cols-[76px_1fr_240px_150px_34px] lg:gap-6 lg:px-6 lg:py-8",
              flooded && "text-accent-foreground",
            )}
          >
            <span
              className={cn(
                "font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-400",
                flooded ? "text-accent-foreground/60" : "text-primary",
              )}
            >
              CS {String(index + 1).padStart(2, "0")}
            </span>

            <span className="text-3xl leading-[0.88] font-black tracking-tighter uppercase md:text-5xl xl:text-6xl">
              {study.name ?? study.title}
            </span>

            <span
              className={cn(
                "font-mono text-[10px] leading-loose tracking-[0.15em] uppercase transition-colors duration-400",
                flooded ? "text-accent-foreground/60" : "text-muted-foreground",
              )}
            >
              {study.tag}
              <br />
              {working.context}
            </span>

            <span
              className={cn(
                "text-2xl leading-none font-black tracking-tighter tabular-nums transition-colors duration-400 lg:text-right lg:text-3xl",
                flooded ? "text-accent-foreground" : "text-primary",
              )}
            >
              {study.metric}
            </span>

            <span
              aria-hidden
              style={isOpen ? { color: study.bgColor } : undefined}
              className={cn(
                "grid size-8 shrink-0 place-items-center border font-mono text-sm leading-none transition-[transform,background-color,border-color,color] duration-450 lg:justify-self-end",
                flooded
                  ? "border-accent-foreground text-accent-foreground"
                  : "border-black/25 text-foreground",
                isOpen ? "rotate-45 bg-accent-foreground" : "bg-transparent",
              )}
            >
              +
            </span>
          </span>
        </button>
      </h3>

      {/* The panel stays mounted whether or not it is open: collapsing it with
          height keeps every case study's working in the markup for crawlers,
          and `inert` keeps the closed ones out of the tab order. */}
      <motion.div
        id={bodyId}
        role="region"
        aria-label={`${study.name ?? study.title} — the working`}
        inert={!isOpen}
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <div
          style={{ borderColor: study.bgColor }}
          className="mx-4 grid gap-6 border-l-[3px] px-4 pt-4 pb-8 lg:mr-0 lg:ml-25 lg:grid-cols-2 lg:gap-11 lg:pr-0 lg:pl-5"
        >
          <div>
            <h4 className="mb-2 font-mono text-[10px] font-semibold tracking-[0.2em] text-primary uppercase">
              What was wrong
            </h4>
            <p className="text-sm leading-loose text-muted-foreground md:text-base">
              {working.problem}
            </p>

            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
              <Link
                href={`/case-study/${study.slug}`}
                className="inline-flex items-center gap-2 border-b-2 border-primary pb-1 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors hover:text-primary"
              >
                Read the full case study
                <ArrowUpRight className="size-3.5" />
              </Link>

              {study.link !== "#" && (
                <a
                  href={study.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-b-2 border-black/15 pb-1 font-mono text-[10px] tracking-[0.18em] text-muted-foreground uppercase transition-colors hover:border-primary hover:text-foreground"
                >
                  Visit the live site
                  <ArrowUpRight className="size-3.5" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="mb-2 font-mono text-[10px] font-semibold tracking-[0.2em] text-primary uppercase">
              What we did about it
            </h4>
            <ul className="grid gap-2.5">
              {working.actions.map((action) => (
                <li
                  key={action}
                  className="border-l-2 border-primary pl-3 text-sm leading-relaxed text-muted-foreground"
                >
                  {action}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CaseStudySelectedWork = () => {
  // First row opens on arrival so the reader never lands on a wall of collapsed
  // rows, but clicking an open row closes it again.
  const [openSlug, setOpenSlug] = useState(caseStudyData[0]?.slug ?? "");

  return (
    <section
      className="w-full bg-background pt-10 md:pt-14 lg:pt-24"
      id="selected-work"
    >
      <Container className="flex flex-col gap-6 px-4 lg:gap-8 lg:px-10">
        <div className="space-y-2">
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionSubHeading subtitle="The detail — open a row to read the working" />
          </motion.div>

          <motion.div
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionTitle title="Expand what you need." className="max-w-4xl" />
          </motion.div>
        </div>

        <div className="mt-2">
          {caseStudyData.map((study, index) => {
            const working = caseStudyWorkings.find(
              (item) => item.slug === study.slug,
            );
            if (!working) return null;

            return (
              <WorkRow
                key={study.id}
                study={study}
                working={working}
                index={index}
                isOpen={openSlug === study.slug}
                onToggle={() =>
                  setOpenSlug(openSlug === study.slug ? "" : study.slug)
                }
              />
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default CaseStudySelectedWork;
